import { TFunction } from 'i18next';
import { all, call, CallEffect, put, PutEffect, takeEvery } from 'redux-saga/effects';
import { getOrganizationLogo } from 'business-dashboard/src/actions/organizationPropertiesActions';
import { RequestActionType, requestError, requestSuccess } from '../../actions/requestActions';
import { selectGroup } from '../../actions/selectGroupActions';
import {
    FETCH_USER_INFO,
    fetchPartnerPropsSuccess,
    fetchDeviceDetails,
    fetchDeviceDetailsSuccess,
    fetchUserInfoError,
    fetchUserInfoSuccess,
    SettingsActionType,
    UPDATE_USER_SETTINGS_SUCCESS,
} from '../../actions/SettingsActions';
import { getUserPersonalInfo, UserPersonalInfoResponse } from '../../api/accountsApiUser';
import { fetchPartnerLogo, getDeviceDetails, getUserInfo } from '../../api/updateSettings';
import { isConsumerAppPage } from '../../commonFunctions';
import { getSelectedGroupFromStorage } from '../../components/findUserType';
import { Consumer } from '../../constants';
import i18next from '../../i18n';
import { GroupType } from '../../models/commonEnums';
import {
    GetDeviceDetailsResponse,
    Group,
    PartnerLogoDetails,
    SelectedGroup,
    UserResponse,
} from '../../models/commonTypeScript';
import { CommonRequestType } from '../../reducers/requestReducer';
import { toErrorType } from '../isErrorType';

export function* updateOrganizationInLocalStorage(groups: Group[]): Generator<PutEffect | SelectedGroup> {
    const currentSelectedGroup: SelectedGroup | null = getSelectedGroupFromStorage();

    // If userGroupId exist in groups -> Do nothing
    const userGroupIds: string[] = groups.map(group => group.id);

    const validUserGroupId: boolean = !!currentSelectedGroup && userGroupIds.includes(currentSelectedGroup.userGroupId);

    if (validUserGroupId) {
        return currentSelectedGroup;
    }

    // If missing value, or shouldBeUpgraded  -> first Org, else Consumer
    const firstFoundAccount: Group | undefined = isConsumerAppPage()
        ? groups.find(group => group.groupType === GroupType.consumer)
        : groups.find(group => group.groupType !== GroupType.consumer) ||
          groups.find(group => group.groupType === GroupType.consumer);

    const selectedGroup = {
        organizationId: firstFoundAccount?.organizationId || Consumer,
        userGroupId: firstFoundAccount?.id || Consumer,
        groupType: firstFoundAccount?.groupType || GroupType.consumer,
    };

    yield put(selectGroup(selectedGroup));
    return selectedGroup;
}

export function* fetchUserInfoSaga(): Generator<
    PutEffect | CallEffect | Promise<UserResponse> | Promise<TFunction>,
    void,
    UserResponse & UserPersonalInfoResponse & SelectedGroup & PartnerLogoDetails
> {
    try {
        const userCognitoResponse = yield call(getUserPersonalInfo);
        const userInfoResponse = yield call(getUserInfo);

        // This should be set explicitly by user, only change if fault is detected
        const { language, groups } = userInfoResponse;
        const { userGroupId } = yield call(updateOrganizationInLocalStorage, groups);

        let languageString = language ? language.slice(0, 2) : language;
        if (languageString === 'nn') languageString = 'nb';
        yield i18next.changeLanguage(languageString);

        if (userInfoResponse.groups.length > 0) {
            const selectedGroup =
                userGroupId && userInfoResponse.groups.find((group: Group) => group.id === userGroupId);

            if (selectedGroup && selectedGroup.groupType === GroupType.partner) {
                yield put(getOrganizationLogo());
            } else if (selectedGroup && selectedGroup.managedByOrganization) {
                const partnerProps = yield call(fetchPartnerLogo);
                yield put(fetchPartnerPropsSuccess(partnerProps));
            }

            yield put(fetchDeviceDetails());
        }

        yield put(
            fetchUserInfoSuccess({
                ...(userInfoResponse as UserResponse),
                name: userCognitoResponse.name,
                email: userCognitoResponse.email,
                language: languageString,
            })
        );
    } catch (error) {
        const asErrorType = toErrorType(error);
        yield put(fetchUserInfoError(asErrorType));
    }
}

export function* fetchDeviceDetailsSaga(): Generator<PutEffect | CallEffect, void, GetDeviceDetailsResponse> {
    try {
        const response: GetDeviceDetailsResponse = yield call(getDeviceDetails);
        yield put(requestSuccess(CommonRequestType.FetchDeviceDetails, RequestActionType.Success));
        yield put(fetchDeviceDetailsSuccess(response));
    } catch (error) {
        const asErrorType = toErrorType(error);
        yield put(requestError(asErrorType, CommonRequestType.FetchDeviceDetails, RequestActionType.Error));
    }
}

export default function* getUserInfoSagas(): Generator {
    yield all([
        takeEvery(FETCH_USER_INFO, fetchUserInfoSaga),
        takeEvery(SettingsActionType.FetchDeviceDetails, fetchDeviceDetailsSaga),
        takeEvery(UPDATE_USER_SETTINGS_SUCCESS, fetchDeviceDetailsSaga),
    ]);
}
